import { useState, useEffect } from "react";
import { json, redirect, type ActionFunctionArgs, type LoaderFunctionArgs } from "@remix-run/node";
import { useLoaderData, Form, Link } from "@remix-run/react";
import { AppProvider, Text, BlockStack, InlineStack, Box, Badge, Icon } from "@shopify/polaris";
import { useIsMobile } from "../../utils/useIsMobile";
import { CheckCircleIcon } from "@shopify/polaris-icons";

import { PricingTable } from "../../components/PricingTable";
import { planCookie } from "../../cookies.server";


export const links = () => [{ rel: "stylesheet", href: "https://unpkg.com/@shopify/polaris@12.0.0/build/esm/styles.css" }];

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);

  if (url.searchParams.get("shop")) {
    throw redirect(`/app?${url.searchParams.toString()}`);
  }

  return json({ showHeader: true });
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const shop = formData.get("shop") as string;
  const plan = formData.get("plan") as string;

  if (shop) {
    // DEMO BYPASS for local testing / screencasts
    const isMockShop = shop.includes("revivify-demo") || (shop.includes("myshopify.com") && process.env.NODE_ENV === "development");

    if (isMockShop) {
      // Use the mock flow for demo stores or local dev
      return redirect(`/mock/install?shop=${shop}${plan ? `&plan=${plan}` : ''}`, {
        headers: {
          "Set-Cookie": await planCookie.serialize(plan),
        },
      });
    }

    // Redirect to the Shopify app login flow
    return redirect(`/auth/login?shop=${shop}${plan ? `&plan=${plan}` : ''}`, {
      headers: {
        "Set-Cookie": await planCookie.serialize(plan),
      },
    });
  }

  return null;
};

export default function LandingPage() {
  const { showHeader } = useLoaderData<typeof loader>();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [shopDomain, setShopDomain] = useState("");
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setIsReady(true);
  }, []);

  const handlePlanClick = (plan: string) => {
    setSelectedPlan(plan);
    document.getElementById('install-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (!isReady) return null;

  return (
    <AppProvider i18n={{}}>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: "100vh", backgroundColor: "#008060", overflowX: 'hidden' }}>
        <style dangerouslySetInnerHTML={{
          __html: `
        html, body { 
          background-color: #008060 !important; 
          margin: 0; 
          padding: 0;
          overflow-x: hidden;
          max-width: 100%;
        }
        *, *::before, *::after { box-sizing: border-box; }
        .video-placeholder {
           width: 100%;
           aspect-ratio: 1280 / 608;
           background-image: url('https://img.youtube.com/vi/ekz1bmVo6Tg/maxresdefault.jpg');
           background-size: cover;
           background-position: center;
           border-radius: 24px;
           display: flex;
           align-items: center;
           justify-content: center;
           position: relative;
           overflow: hidden;
           box-shadow: 0 40px 100px rgba(0, 0, 0, 0.2);
           border: 8px solid #008060;
           cursor: pointer;
           transition: all 0.3s ease;
        }
        .video-placeholder::before {
           content: '';
           position: absolute;
           inset: 0;
           background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 100%);
           transition: opacity 0.3s ease;
        }
        .video-placeholder:hover { transform: scale(1.02); }
        .video-placeholder:hover::before {
           background: linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.7) 100%);
        }
        .play-button {
           width: 90px; height: 90px;
           background: rgba(255, 255, 255, 0.2);
           backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
           border-radius: 50%; display: flex; align-items: center; justify-content: center;
           border: 2.5px solid white; color: white; z-index: 2;
           transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
           box-shadow: 0 10px 20px rgba(0,0,0,0.2);
        }
        .video-placeholder:hover .play-button {
           background: rgba(255, 255, 255, 0.4);
           transform: scale(1.1); box-shadow: 0 15px 30px rgba(0,0,0,0.3);
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
        .video-frame-container { animation: fadeIn 0.5s ease-out forwards; }
        .lp-nav-links { display: flex; }
        .lp-hero { display: flex; gap: 60px; align-items: flex-start; }
        .lp-hero-text { flex: 1.2; min-width: 0; }
        .lp-hero-media { flex: 1.5; min-width: 0; }
        .lp-hero-title { font-size: 64px; font-weight: 800; line-height: 1.1; letter-spacing: -0.04em; color: #111827; }
        .lp-cta-row { display: flex; gap: 16px; }
        .lp-cta-btn { flex: 1; min-width: 0; }
        .lp-roi-row { display: flex; gap: 32px; align-items: stretch; }
        .lp-intent-row { display: flex; gap: 32px; align-items: center; }
        .lp-integrity-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .lp-install-row { display: flex; gap: 40px; align-items: stretch; }
        .lp-section-pad { padding: 20px 40px; }
        @media (max-width: 767px) {
          .lp-nav-links { display: none; }
          .lp-hero { flex-direction: column; gap: 24px; }
          .lp-hero-text { flex: none; width: 100%; max-width: 100%; min-width: 0; }
          .lp-hero-media { flex: none; width: 100%; max-width: 100%; min-width: 0; }
          .lp-hero-title { font-size: 32px; letter-spacing: -0.01em; word-break: break-word; overflow-wrap: break-word; line-height: 1.2; }
          .lp-cta-row { flex-direction: column; gap: 12px; }
          .lp-cta-btn { flex: none; width: 100%; }
          .lp-roi-row { flex-direction: column; gap: 16px; }
          .lp-intent-row { flex-direction: column; gap: 24px; }
          .lp-integrity-grid { grid-template-columns: 1fr; gap: 16px; }
          .lp-install-row { flex-direction: column; gap: 24px; }
          .lp-section-pad { padding: 12px 16px; }
          .video-placeholder { aspect-ratio: 16/9 !important; border-radius: 16px; }
          .Polaris-Page { padding: 0 !important; }
          .Polaris-BlockStack, .Polaris-InlineStack { min-width: 0; }
        }
      `}} />

        <div style={{ flex: 1, backgroundColor: "#f9fafb" }}>
          {showHeader && (
            <div style={{
              padding: isMobile ? '0.875rem 1rem' : '1.25rem 2.5rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'white',
              borderBottom: '1px solid #f1f2f4',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '40px', height: '40px',
                  backgroundColor: '#008060', borderRadius: '10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', flexShrink: 0
                }}>
                  <div style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', transform: 'scale(2.0)' }}>
                    <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                      <path d="M16.7071 5.29289C17.0976 5.68342 17.0976 6.31658 16.7071 6.70711L8.70711 14.7071C8.31658 15.0976 7.68342 15.0976 7.29289 14.7071L3.29289 10.7071C2.90237 10.3166 2.90237 9.68342 3.29289 9.29289C3.68342 8.90237 4.31658 8.90237 4.70711 9.29289L8 12.5858L15.2929 5.29289C15.6834 4.90237 16.3166 4.90237 16.7071 5.29289Z" fill="white" />
                    </svg>
                  </div>
                </div>
                <BlockStack gap="0">
                  <Text variant={isMobile ? 'headingMd' : 'headingLg'} as="h1" fontWeight="bold">Revivify AI</Text>
                  {!isMobile && <Text variant="bodySm" as="p" tone="subdued">AI revenue recovery for Shopify merchants</Text>}
                </BlockStack>
              </div>

              <div className="lp-nav-links" style={{ gap: '24px', alignItems: 'center' }}>
                <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#637381', fontSize: '15px', fontWeight: '500' }}>Features</button>
                <button onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#637381', fontSize: '15px', fontWeight: '500' }}>Pricing</button>
                <button onClick={() => document.getElementById('integrations')?.scrollIntoView({ behavior: 'smooth' })}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#637381', fontSize: '15px', fontWeight: '500' }}>Integrations</button>
                <Link to="/why-revivify" style={{ textDecoration: 'none', color: '#637381', fontSize: '15px', fontWeight: '500' }}>Why Revivify?</Link>
              </div>

              {isMobile && (
                <button
                  onClick={() => document.getElementById('install-form')?.scrollIntoView({ behavior: 'smooth' })}
                  style={{ backgroundColor: '#008060', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                  Install
                </button>
              )}
            </div>
          )}

          <div style={{ maxWidth: "1200px", margin: "0 auto", padding: isMobile ? '16px 16px' : '20px 40px', width: '100%', overflowX: 'hidden' }}>
            <BlockStack gap="200">
              <div id="features" className="lp-hero" style={{ scrollMarginTop: '100px' }}>
                <div className="lp-hero-text">
                  <BlockStack gap="600">
                    <BlockStack gap="400">
                      <div className="lp-hero-title">
                        Recover revenue. <br />
                        <span style={{ color: "#008060" }}>Prevent chargebacks.</span> <br />
                        With AI.
                      </div>
                      <div style={{ fontSize: isMobile ? '16px' : '20px', lineHeight: "1.5", color: "#454f5b", maxWidth: '100%' }}>
                        <span style={{ display: "block", fontSize: "16px", fontWeight: "600", color: "#008060", letterSpacing: "0.02em", marginBottom: "16px", padding: "10px 0", borderBottom: "2px solid #e6f2ed" }}>
                          Most tools react after a payment fails. We act before the return becomes a cancellation.
                        </span>
                        Revivify AI decodes customer intent to turn every return into a retention win. Predict churn, adapt in real-time, and save your revenue while keeping you in total control.
                      </div>
                    </BlockStack>
                  </BlockStack>
                </div>

                <div className="lp-hero-media">
                  <BlockStack gap="600">
                    <div style={{ position: 'relative', marginTop: '-20px' }}>
                      {!isPlaying ? (
                        <div className="video-placeholder" onClick={() => setIsPlaying(true)} style={{
                          aspectRatio: '16/7.7', cursor: 'pointer', position: 'relative',
                          borderRadius: '24px', overflow: 'hidden',
                          boxShadow: '0 40px 100px rgba(0, 0, 0, 0.2)',
                          border: '4px solid #008060',
                          backgroundColor: 'black'
                        }}>
                          <div className="play-button" style={{
                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                            zIndex: 10, backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: '50%',
                            width: '80px', height: '80px', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', color: '#008060'
                          }}>
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                          <img
                            src="https://img.youtube.com/vi/ekz1bmVo6Tg/maxresdefault.jpg"
                            alt="Revivify Demo Preview"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </div>
                      ) : (
                        <div style={{
                          aspectRatio: '16/7.7', borderRadius: '24px', overflow: 'hidden',
                          boxShadow: '0 40px 100px rgba(0, 0, 0, 0.2)',
                          border: '4px solid #008060'
                        }}>
                          <video
                            width="100%"
                            height="100%"
                            autoPlay
                            loop
                            muted
                            playsInline
                            controls
                            style={{ objectFit: 'cover' }}
                          >
                            <source src="/revivify.mp4" type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      )}
                    </div>

                    <BlockStack gap="400">
                      <div className="lp-cta-row">
                        <button className="lp-cta-btn"
                          onClick={() => document.getElementById('install-form')?.scrollIntoView({ behavior: 'smooth' })}
                          style={{
                            backgroundColor: '#008060', color: 'white',
                            padding: isMobile ? '14px 20px' : '16px 32px',
                            borderRadius: '12px', border: 'none',
                            fontSize: isMobile ? '16px' : '18px', fontWeight: '600', cursor: 'pointer',
                            boxShadow: '0 4px 14px rgba(0, 128, 96, 0.25)',
                            transition: 'transform 0.2s ease', width: '100%'
                          }}
                        >
                          Install App
                        </button>
                        <button className="lp-cta-btn"
                          onClick={() => window.location.href = '/public-demo'}
                          style={{
                            backgroundColor: 'white', color: '#111827',
                            padding: isMobile ? '14px 20px' : '16px 32px',
                            borderRadius: '12px', border: '1px solid #e1e3e5',
                            fontSize: isMobile ? '16px' : '18px', fontWeight: '600', cursor: 'pointer',
                            transition: 'all 0.2s ease', width: '100%'
                          }}
                        >
                          View Interactive Demo
                        </button>
                      </div>

                      <InlineStack gap="200" blockAlign="center" align="center">
                        <Text variant="bodySm" tone="subdued">REQUIRES</Text>
                        <Badge tone="info">Recharge</Badge>
                        <Text variant="bodySm" tone="subdued">and</Text>
                        <Badge tone="info">Loop Returns</Badge>
                        <Text variant="bodySm" tone="subdued">or</Text>
                        <Badge tone="info">AfterShip</Badge>
                      </InlineStack>
                    </BlockStack>
                  </BlockStack>
                </div>
              </div>

              {/* ECONOMICS & ROI SECTION */}
              <Box paddingBlockStart="200" paddingBlockEnd="200" background="bg-surface-secondary">
                <div style={{ maxWidth: "1200px", margin: "0 auto" }} className="lp-section-pad">
                  <BlockStack gap="400">
                    <div style={{ textAlign: 'center' }}>
                      <Badge tone="success">ROI & PROFITABILITY</Badge>
                      <div style={{ marginTop: '16px' }}>
                        <Text variant="heading2xl" as="h2">The Economics of Retention</Text>
                        <div style={{ marginTop: '8px' }}>
                          <Text variant="bodyLg" as="p" tone="success" fontWeight="bold">
                            ROI: One saved subscription could pay for the entire month.
                          </Text>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Row: The Math & ROI */}
                    <div className="lp-roi-row">
                      {/* The 5% Rule */}
                      <div style={{
                        flex: 1,
                        backgroundColor: 'white',
                        padding: '24px',
                        borderRadius: '24px',
                        border: '2px solid #e1e3e5',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
                      }}>
                        <BlockStack gap="300">
                          <InlineStack gap="300" align="start" blockAlign="center">
                            <div style={{
                              width: '40px', height: '40px', backgroundColor: '#f6ffed',
                              borderRadius: '10px', display: 'flex', alignItems: 'center',
                              justifyContent: 'center', color: '#008060'
                            }}>
                              <Icon source={CheckCircleIcon} tone="success" />
                            </div>
                            <Text variant="headingLg" as="h3">The "5% Rule"</Text>
                          </InlineStack>
                          <Text variant="bodyLg" as="p" tone="subdued">
                            Research shows that a <strong>5% increase in customer retention</strong> can increase profits by <strong>25% to 95%</strong>.
                          </Text>
                          <Box paddingBlockStart="200">
                            <Text variant="bodyMd" as="p">
                              Because Revivify AI operates at the exact moment of a return (the highest risk of churn), merchants using Revivify capture that 5% better than any generic email flow.
                            </Text>
                          </Box>
                        </BlockStack>
                      </div>

                      {/* Quantifying the Save */}
                      <div style={{
                        flex: 1,
                        backgroundColor: 'white',
                        padding: '24px',
                        borderRadius: '24px',
                        border: '2px solid #e1e3e5',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
                      }}>
                        <BlockStack gap="300">
                          <InlineStack gap="300" align="start" blockAlign="center">
                            <div style={{
                              width: '40px', height: '40px', backgroundColor: '#e6f7ff',
                              borderRadius: '10px', display: 'flex', alignItems: 'center',
                              justifyContent: 'center', color: '#008060'
                            }}>
                              <Text variant="headingMd" as="span" tone="success">$</Text>
                            </div>
                            <Text variant="headingLg" as="h3">Quantifying the "Save"</Text>
                          </InlineStack>
                          <BlockStack gap="200">
                            <Text variant="bodyMd" as="p"><strong>The Churn Reality:</strong> The average Shopify subscription churn is ~3.4% per month.</Text>
                            <Text variant="bodyMd" as="p"><strong>The Math:</strong> For a brand doing $500k/year in subscriptions, they are losing <strong>$17,000 every month</strong> to churn.</Text>
                            <Text variant="bodyMd" as="p"><strong>Our Impact:</strong> If Revivify AI "recovers" just 10% of those churned customers, we are adding <strong>$20,000+ in ARR</strong> to your store.</Text>
                            <Text variant="bodyMd" as="p"><strong>ROI:</strong> One saved subscription could pay for the entire month.</Text>
                          </BlockStack>
                        </BlockStack>
                      </div>
                    </div>
                  </BlockStack>
                </div>
              </Box>

              {/* AI INTENT SECTION */}
              <Box paddingBlockStart="200" paddingBlockEnd="200" background="bg-surface-secondary">
                <div style={{ maxWidth: "1200px", margin: "0 auto" }} className="lp-section-pad">
                  <div className="lp-intent-row">
                    <div style={{ flex: 1 }}>
                      <BlockStack gap="400">
                        <Badge tone="attention">AI POWERED</Badge>
                        <Text variant="heading2xl" as="h2">Deciphering "Retention Intent"</Text>
                        <Text variant="bodyLg" as="p" tone="subdued">
                          Standard return apps only read data. Revivify AI reads between the lines.
                          Our engine identifies when a customer wants to leave vs. when they just want a change.
                        </Text>
                        <div style={{ marginTop: '20px' }}>
                          <BlockStack gap="300">
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#008060' }}></div>
                              <Text variant="bodyMd" as="p"><b>Automatically Swaps</b> products based on flavor/scent complaints.</Text>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#008060' }}></div>
                              <Text variant="bodyMd" as="p"><b>Suggests Skips</b> for customers who are "too stocked up."</Text>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#008060' }}></div>
                              <Text variant="bodyMd" as="p"><b>Prevents $40 Support Tickets</b> by automating complex exchange requests.</Text>
                            </div>
                          </BlockStack>
                        </div>
                      </BlockStack>
                    </div>

                    <div style={{ flex: 1, backgroundColor: 'white', padding: '24px', borderRadius: '24px', border: '2px solid #e1e3e5', boxShadow: '0 20px 40px rgba(0,0,0,0.05)' }}>
                      <BlockStack gap="300">
                        {/* Customer Side */}
                        <div style={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
                          <BlockStack gap="100">
                            <Text variant="bodyXs" tone="subdued" fontWeight="bold">CUSTOMER NOTE</Text>
                            <div style={{ backgroundColor: '#f4f6f8', padding: '12px 16px', borderRadius: '12px', border: '1px solid #dfe3e8' }}>
                              <Text variant="bodySm" as="p">
                                "I really <b>love the brand</b>, but I <b>hate the Vanilla flavor</b>. It's too sweet for me. Canceling for now."
                              </Text>
                            </div>
                          </BlockStack>
                        </div>

                        <div style={{ height: '20px', width: '1px', backgroundColor: '#dfe3e8', margin: '0 auto' }}></div>

                        {/* AI Side */}
                        <div style={{ alignSelf: 'flex-end', maxWidth: '85%', width: '100%' }}>
                          <BlockStack gap="100">
                            <div style={{ textAlign: 'right' }}>
                              <Text variant="bodyXs" tone="success" fontWeight="bold">REVIVIFY AI ACTION</Text>
                            </div>
                            <div style={{ backgroundColor: '#008060', padding: '12px 16px', borderRadius: '12px', color: 'white', boxShadow: '0 4px 12px rgba(0,128,96,0.2)' }}>
                              <BlockStack gap="100">
                                <Text variant="bodySm" as="p"><b>Action: SWAP_PRODUCT</b></Text>
                                <Text variant="bodyXs" as="p">Suggested: "Sea Salt Dark Chocolate" (Top Rated)</Text>
                                <div style={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.2)', margin: '4px 0' }}></div>
                                <Text variant="bodyXs" as="p"><b>Result:</b> Subscription saved. Churn prevented automatically.</Text>
                              </BlockStack>
                            </div>
                          </BlockStack>
                        </div>
                      </BlockStack>
                    </div>
                  </div>
                </div>
              </Box>

              {/* INTEGRATION INTEGRITY SECTION */}
              <Box paddingBlockStart="0" paddingBlockEnd="0">
                <div style={{ maxWidth: "1200px", margin: "0 auto" }} className="lp-section-pad">
                  <BlockStack gap="600">
                    <div style={{ textAlign: 'center' }}>
                      <Badge tone="attention">THE INTEGRITY GAP</Badge>
                      <div style={{ marginTop: '16px' }}>
                        <Text variant="heading2xl" as="h2">Stop the Integration Nightmares</Text>
                      </div>
                      <Text variant="bodyLg" as="p" tone="subdued">
                        Recharge, Loop Returns, and AfterShip are powerful tools — but their standard integration often leaves "ghost data" that costs you hours in manual support.
                        Revivify AI acts as the intelligent glue that ensures your books always match.
                      </Text>
                    </div>

                    <div className="lp-integrity-grid">
                      {/* Ghost Refunds Card */}
                      <div style={{
                        backgroundColor: 'white',
                        padding: '32px',
                        borderRadius: '24px',
                        border: '2px solid #e1e3e5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '280px'
                      }}>
                        <div style={{ height: '130px' }}>
                          <BlockStack gap="300">
                            <div style={{ textAlign: 'left' }}>
                              <Badge tone="critical">THE NIGHTMARE</Badge>
                            </div>
                            <Text variant="headingLg" as="h3">"Ghost Refunds"</Text>
                            <Text variant="bodyMd" as="p" tone="subdued">
                              Loop issues a refund, but Recharge never stops. You end up shipping free items and bleeding COGS.
                            </Text>
                          </BlockStack>
                        </div>
                        <Box paddingBlockStart="200">
                          <div style={{ color: '#008060', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginBottom: '8px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                            <div style={{ marginRight: '2px', display: 'flex', alignItems: 'center' }}>
                              <Icon source={CheckCircleIcon} tone="success" />
                            </div>
                            <span>Revivify Solution: State Healing</span>
                          </div>
                          <Text variant="bodySm" as="p">We poll and verify actual state vs. intent. We don't stop until the sync is 100% verified.</Text>
                        </Box>
                      </div>

                      {/* Data Persistence Card */}
                      <div style={{
                        backgroundColor: 'white',
                        padding: '32px',
                        borderRadius: '24px',
                        border: '2px solid #e1e3e5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '280px'
                      }}>
                        <div style={{ height: '130px' }}>
                          <BlockStack gap="300">
                            <div style={{ textAlign: 'left' }}>
                              <Badge tone="critical">THE NIGHTMARE</Badge>
                            </div>
                            <Text variant="headingLg" as="h3">"Persistence Zombies"</Text>
                            <Text variant="bodyMd" as="p" tone="subdued">
                              Old checkout contracts "poison" new return attempts, defaulting to incorrect shipping or legacy pricing.
                            </Text>
                          </BlockStack>
                        </div>
                        <Box paddingBlockStart="200">
                          <div style={{ color: '#008060', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginBottom: '8px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                            <div style={{ marginRight: '2px', display: 'flex', alignItems: 'center' }}>
                              <Icon source={CheckCircleIcon} tone="success" />
                            </div>
                            <span>Revivify Solution: Contract Cleansing</span>
                          </div>
                          <Text variant="bodySm" as="p">We perform real-time metadata lookups on every return, bypassing stale contracts for a clean slate.</Text>
                        </Box>
                      </div>

                      {/* Bleeding Margins Card */}
                      <div style={{
                        backgroundColor: 'white',
                        padding: '32px',
                        borderRadius: '24px',
                        border: '2px solid #e1e3e5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '280px'
                      }}>
                        <div style={{ height: '130px' }}>
                          <BlockStack gap="300">
                            <div style={{ textAlign: 'left' }}>
                              <Badge tone="critical">THE NIGHTMARE</Badge>
                            </div>
                            <Text variant="headingLg" as="h3">"Bleeding Margins"</Text>
                            <Text variant="bodyMd" as="p" tone="subdued">
                              Manual labor costs of a support team spending 10+ hours a week "cleaning up" integration failures.
                            </Text>
                          </BlockStack>
                        </div>
                        <Box paddingBlockStart="200">
                          <div style={{ color: '#008060', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginBottom: '8px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                            <div style={{ marginRight: '2px', display: 'flex', alignItems: 'center' }}>
                              <Icon source={CheckCircleIcon} tone="success" />
                            </div>
                            <span>Revivify Solution: Inventory Guards</span>
                          </div>
                          <Text variant="bodySm" as="p">Auto-halts on currency/inventory mismatch. We alert you BEFORE the mistake hits your accounting.</Text>
                        </Box>
                      </div>
                    </div>
                  </BlockStack>
                </div>
              </Box>

              {/* INSTALL FORM SECTION */}
              <Box paddingBlockStart="0" paddingBlockEnd="400">
                <div style={{ maxWidth: "1200px", margin: "0 auto" }} className="lp-section-pad">
                  <BlockStack gap="400">
                    <div style={{ textAlign: 'center' }}>
                      <Text variant="headingXl" as="h2">Ready to save your revenue?</Text>
                      <Text variant="bodyLg" tone="subdued">Join 500+ top Shopify Plus stores turning returns into Recoveries.</Text>
                    </div>

                    <div className="lp-install-row">
                      <div id="install-form" style={{
                        flex: 1.1, backgroundColor: 'white', borderRadius: '24px',
                        border: selectedPlan ? '2px solid #008060' : '2px solid #e1e3e5',
                        padding: '40px', boxShadow: '0 25px 50px rgba(0, 0, 0, 0.1)', scrollMarginTop: '120px'
                      }}>
                        <Form method="post">
                          <BlockStack gap="600">
                            <BlockStack gap="400">
                              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: selectedPlan ? '#008060' : '#8c9196', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold' }}>1</div>
                                <Text variant="headingMd" as="span">Select a plan below</Text>
                                {selectedPlan && <Icon source={CheckCircleIcon} tone="success" />}
                              </div>
                              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', opacity: selectedPlan ? 1 : 0.7 }}>
                                <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: selectedPlan && shopDomain ? '#008060' : '#8c9196', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold' }}>2</div>
                                <Text variant="headingMd" as="span">Enter your shop domain</Text>
                              </div>
                            </BlockStack>
                            <div style={{ position: 'relative' }}>
                              <input type="text" name="shop" placeholder="your-store.myshopify.com"
                                value={shopDomain} onChange={(e) => setShopDomain(e.target.value)}
                                disabled={!selectedPlan}
                                style={{
                                  width: '100%', padding: '16px 20px', borderRadius: '12px', border: '1px solid #b2b5b9',
                                  fontSize: '18px', color: '#111827', backgroundColor: !selectedPlan ? '#f6f6f7' : 'white',
                                  outline: 'none', cursor: !selectedPlan ? 'not-allowed' : 'text', boxSizing: 'border-box'
                                }}
                                required
                              />
                              <input type="hidden" name="plan" value={selectedPlan || ""} />
                              <div style={{ fontSize: '13px', color: '#637381', marginTop: '8px' }}>e.g: revivify-demo.myshopify.com</div>
                            </div>
                            <button type="submit" disabled={!selectedPlan || !shopDomain}
                              style={{
                                width: '100%', backgroundColor: (!selectedPlan || !shopDomain) ? '#8c9196' : '#008060',
                                color: 'white', padding: '18px', borderRadius: '12px', border: 'none', fontSize: '20px',
                                fontWeight: '600', cursor: (!selectedPlan || !shopDomain) ? 'not-allowed' : 'pointer',
                                boxShadow: '0 4px 14px rgba(0, 128, 96, 0.25)'
                              }}>
                              {!selectedPlan ? 'Select Plan First' : 'Complete Installation'}
                            </button>
                          </BlockStack>
                        </Form>
                      </div>

                      <div style={{ flex: 0.9, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ backgroundColor: '#f6ffed', padding: '48px', borderRadius: '24px', border: '2px solid #b7eb8f', boxShadow: '0 20px 40px rgba(0,0,0,0.06)', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <BlockStack gap="600">
                            <div style={{ alignSelf: 'flex-start' }}>
                              <Badge tone="success">The Revivify Growth Edge</Badge>
                            </div>
                            <BlockStack gap="400">
                              <Text variant="headingXl" as="h3">AI-Powered Retention</Text>
                              <div style={{ fontSize: '22px', lineHeight: '1.4', color: '#454f5b', fontStyle: 'italic' }}>
                                "Most apps just process returns. Revivify AI decodes customer behavior to proactively save revenue and build long-term loyalty."
                              </div>
                            </BlockStack>
                            <div style={{ borderTop: '1px solid #b7eb8f', paddingTop: '24px' }}>
                              <InlineStack gap="400" align="start" blockAlign="center">
                                <div style={{ width: '32px', height: '32px' }}>
                                  <Icon source={CheckCircleIcon} tone="success" />
                                </div>
                                <Text variant="headingMd" as="p"><b>7-Day</b> Free Trial on all plans</Text>
                              </InlineStack>
                            </div>
                          </BlockStack>
                        </div>
                      </div>
                    </div>
                  </BlockStack>
                </div>
              </Box>

              <div id="pricing" style={{ display: "flex", justifyContent: "center", width: "100%", scrollMarginTop: '100px' }}>
                <PricingTable isInstallFlow={true} selectedPlan={selectedPlan || undefined} onSelect={handlePlanClick} />
              </div>

              <div id="integrations" style={{ marginTop: '32px', paddingBottom: '32px', scrollMarginTop: '100px' }}>
                <BlockStack gap="400">
                  <Text variant="headingXl" as="h2" alignment="center">Works with your favorite apps</Text>
                  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "12px", maxWidth: "1200px", margin: "0 auto", padding: "0 20px" }}>
                    {[
                      { name: "Recharge", logo: "https://img.logokit.com/rechargepayments.com?token=pk_fr9f78c3f7c316db74a419", supported: true },
                      { name: "Loop Returns", logo: "https://img.logokit.com/loopreturns.com?token=pk_fr9f78c3f7c316db74a419", supported: true },
                      { name: "AfterShip", logo: "https://img.logokit.com/aftership.com?token=pk_fr9f78c3f7c316db74a419", supported: true },
                      { name: "Gorgias", logo: "https://img.logokit.com/gorgias.com?token=pk_fr9f78c3f7c316db74a419" },
                      { name: "Klaviyo", logo: "https://img.logokit.com/klaviyo.com?token=pk_fr9f78c3f7c316db74a419" },
                      { name: "LoyaltyLion", logo: "https://img.logokit.com/loyaltylion.com?token=pk_fr9f78c3f7c316db74a419" }
                    ].map((integ, i) => (
                      <div key={i} style={{
                        backgroundColor: 'white',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '2px solid #e1e3e5',
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                        minWidth: "155px",
                        boxShadow: "0 8px 16px rgba(0, 0, 0, 0.04)"
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                          <img src={integ.logo} alt={integ.name} style={{ height: "24px", width: "24px", objectFit: "contain", filter: integ.supported ? 'none' : 'grayscale(100%)', opacity: integ.supported ? 1 : 0.6 }} />
                          <Text variant="bodyMd" as="span" fontWeight="bold" tone={integ.supported ? "default" : "subdued"}>{integ.name}</Text>
                        </div>
                        {!integ.supported && (
                          <div style={{ fontSize: '10px', color: '#6d7175', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Coming Soon
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: '24px', textAlign: 'center' }}>
                    <Text variant="bodyMd" as="p" tone="subdued">
                      Works with <strong>Recharge</strong> + <strong>Loop Returns</strong> or <strong>AfterShip Returns</strong> — connect whichever return platform you use.
                    </Text>
                  </div>
                </BlockStack>
              </div>
            </BlockStack>
          </div>
        </div>

        <div style={{ backgroundColor: '#008060', padding: '40px 40px', color: 'white' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
            <BlockStack gap="400" inlineAlign="center">
              <Text variant="bodySm" as="p">© 2026 Revivify AI. Built for Shopify merchants.</Text>
              <InlineStack gap="600" align="center">
                <Link to="/why-revivify" style={{ textDecoration: 'none', color: 'white', opacity: 0.9 }}><Text variant="bodySm" as="p">Why Revivify?</Text></Link>
                <Link to="/privacy" style={{ textDecoration: 'none', color: 'white', opacity: 0.9 }}><Text variant="bodySm" as="p">Privacy Policy</Text></Link>
                <Link to="/terms" style={{ textDecoration: 'none', color: 'white', opacity: 0.9 }}><Text variant="bodySm" as="p">Terms of Service</Text></Link>
              </InlineStack>
            </BlockStack>
          </div>
        </div>
      </div>
    </AppProvider>
  );
}
